import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { HC } from "../hr/theme";
import {
  LayoutDashboard, Users, Filter, Gift, Megaphone, Route, Activity,
  FileBarChart2, Settings as SettingsIcon, ArrowLeft, Crown, Menu as MenuIcon, Copy,
} from "lucide-react";

const NAV = [
  { path: "", label: "CRM Dashboard", Icon: LayoutDashboard },
  { path: "customers", label: "Customers", Icon: Users },
  { path: "duplicates", label: "Duplicates", Icon: Copy },
  { path: "segments", label: "Segments", Icon: Filter },
  { path: "loyalty", label: "Loyalty", Icon: Gift },
  { path: "campaigns", label: "Campaigns", Icon: Megaphone },
  { path: "journeys", label: "Journeys", Icon: Route },
  { path: "activity", label: "Customer Activity", Icon: Activity },
  { path: "reports", label: "Reports", Icon: FileBarChart2 },
  { path: "settings", label: "CRM Settings", Icon: SettingsIcon },
];

const META = {
  "": { title: "CRM & Loyalty", sub: "One customer record · one identity · one loyalty wallet." },
  customers: { title: "Customers", sub: "Unified profiles across POS, website and mobile app." },
  duplicates: { title: "Duplicate Management", sub: "Review, compare and merge accounts sharing an identifier." },
  segments: { title: "Segments", sub: "Dynamic audiences recalculated from live transaction data." },
  loyalty: { title: "Loyalty Engine", sub: "Accounts, ledger, earning rules, redemption and tiers." },
  campaigns: { title: "Campaigns", sub: "Targeted communications routed through your marketing integration." },
  journeys: { title: "Customer Journeys", sub: "Deterministic trigger → condition → wait → action flows." },
  activity: { title: "Customer Activity", sub: "Every registration, purchase, loyalty and campaign event." },
  reports: { title: "CRM Reports", sub: "Customer · segmentation · loyalty · marketing." },
  settings: { title: "CRM Settings", sub: "Probation, expiry, consent rules and integrations." },
};

export default function CRMLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [integration, setIntegration] = useState(null);

  useEffect(() => {
    api.get("/crm/settings").then((s) => setIntegration(s.integrations)).catch(() => {});
  }, []);

  const path = loc.pathname.replace(/^\/crm\/?/, "").split("/")[0];
  const meta = META[path] || META[""];
  const connected = integration && Object.values(integration).some((v) => v?.enabled);

  const navStyle = ({ isActive }) => ({
    background: isActive ? HC.burgundySoft : "transparent",
    color: isActive ? HC.burgundy : HC.text,
    borderLeft: isActive ? `3px solid ${HC.gold}` : "3px solid transparent",
  });

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: HC.canvas }}>
      <aside data-testid="crm-sidebar"
        className={`w-[248px] ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static z-40 h-full flex flex-col transition-transform duration-300`}
        style={{ background: HC.panel, borderRight: `1px solid ${HC.border}` }}>
        <div className="h-[84px] flex items-center gap-3 px-5">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: HC.burgundy }}>
            <Crown size={21} color={HC.gold} strokeWidth={2.2} />
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-brand font-bold text-[26px] tracking-tight" style={{ color: HC.burgundy }}>Sir Monty</div>
            <div className="text-[10px] font-bold tracking-[0.2em]" style={{ color: HC.soft }}>ERP</div>
          </div>
        </div>
        <div className="px-5 pt-2 pb-3 text-[10px] font-bold tracking-[0.2em]" style={{ color: HC.gold }}>CRM &amp; LOYALTY</div>
        <nav className="flex-1 overflow-y-auto px-3 space-y-1">
          {NAV.map(({ path: p, label, Icon }) => (
            <NavLink key={label} to={`/crm/${p}`} end={p === ""} onClick={() => setMobileOpen(false)}
              data-testid={`crm-nav-${p || "dashboard"}`}
              className="flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-semibold hover:bg-[#FBF8F2]"
              style={navStyle}>
              <Icon size={17} /> {label}
            </NavLink>
          ))}
        </nav>
        <button onClick={() => navigate("/")} data-testid="crm-back-erp"
          className="m-3 flex items-center gap-2 px-3 h-11 rounded-xl text-sm font-semibold hover:bg-[#FBF8F2]"
          style={{ color: HC.muted }}>
          <ArrowLeft size={16} /> Back to ERP
        </button>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <header className="sticky top-0 z-30 px-5 lg:px-8 py-4 flex items-center gap-3 flex-wrap"
          style={{ background: HC.panel, borderBottom: `1px solid ${HC.border}` }}>
          <button className="lg:hidden" onClick={() => setMobileOpen((o) => !o)} data-testid="crm-mobile-toggle">
            <MenuIcon size={20} />
          </button>
          <div className="min-w-0">
            <h1 className="font-brand font-bold text-lg md:text-xl leading-tight" style={{ color: HC.text }}>{meta.title}</h1>
            <div className="text-[12px]" style={{ color: HC.soft }}>{meta.sub}</div>
          </div>
          <div className="flex-1" />
          <span data-testid="crm-integration-status"
            className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
            style={connected
              ? { background: HC.greenTint, color: HC.green }
              : { background: HC.amberTint, color: HC.amber }}>
            {connected ? "Marketing integration connected" : "No marketing integration connected"}
          </span>
          <span className="text-[12px] font-semibold" style={{ color: HC.muted }}>{user?.name}</span>
        </header>
        <div className="p-5 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
